import { useMemo } from "react";
import { useState } from "react";
import {
  PageShell,
  PageHeader,
  ListView,
  ListSkeleton,
  EmptyState,
  StatCard,
  Modal,
} from "../../../Components/shared";
import {
  FaIndustry,
  FaBoxOpen,
  FaVial,
  FaTools,
  FaCoffee,
  FaTabletAlt,
} from "react-icons/fa";
import useAuth from "../../../Hooks/useAuth";
import usePermission from "../../../Hooks/usePermission";
import RowActionButton from "../../../Components/factory/storageRoles/RowActionButton";
import { useAlert } from "../../../context/AlertContext";
import {
  useGetFactoryOverviewQuery,
  useCancelFactoryOperationLateMutation,
} from "../../../store";

const TABLE_HEADERS = [
  { label: "ZAMAN", className: "w-[14%]" },
  { label: "ÇALIŞAN", className: "w-[22%]" },
  { label: "EYLEM", className: "w-[14%]" },
  { label: "HEDEF", className: "w-[26%]" },
  { label: "BÖLGE", className: "w-[12%]" },
  { label: "DURUM", className: "w-[12%]" },
];

const formatTime = (iso) => {
  if (!iso) return "-";
  const d = new Date(iso);
  return d.toLocaleString("tr-TR", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

/**
 * Fabrika Genel Bakış — özet sayılar + son işlemler.
 * 30 sn'de bir tazelenir (tablet işlemleri kısa aralıklarla akar).
 */
export default function FactoryOverviewPage() {
  const { auth } = useAuth();
  const token = auth?.accessToken;
  const { hasPermission } = usePermission();
  const canCancel = hasPermission("factory.operation.cancel.admin");
  const { showSuccess, showError } = useAlert();

  // Geç iptal (BR-046): sebep zorunlu
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelOperation, { isLoading: cancelling }] =
    useCancelFactoryOperationLateMutation();

  const submitCancel = async () => {
    try {
      await cancelOperation({
        token,
        id: cancelTarget._id,
        reason: cancelReason.trim(),
      }).unwrap();
      showSuccess("İşlem iptal edildi");
      setCancelTarget(null);
      setCancelReason("");
    } catch (err) {
      showError(err?.data?.message || "İptal edilemedi");
    }
  };

  const { data, isLoading, isFetching } = useGetFactoryOverviewQuery(
    { token },
    { pollingInterval: 30_000, skip: !token }
  );

  const loading = isLoading || (isFetching && !data);

  const stats = useMemo(() => {
    if (!data) return [];
    return [
      { label: "Bugünkü İşlem", value: data.todayOperationCount },
      { label: "Çevrimiçi Tablet", value: `${data.onlineKioskCount}/${data.kioskCount}` },
    ];
  }, [data]);

  const cards = [
    {
      title: "Hazır Parça",
      count: data?.unitCounts?.hazir ?? 0,
      icon: <FaBoxOpen />,
      color: "emerald",
    },
    {
      title: "Üretim Sürecinde",
      count: data?.unitCounts?.uretim ?? 0,
      icon: <FaVial />,
      color: "amber",
    },
    {
      title: "Aktif Montaj / Bakım",
      count: data?.activeBuildCount ?? 0,
      icon: <FaTools />,
      color: "indigo",
    },
    {
      title: "Üretilen Makine",
      count: data?.machineCount ?? 0,
      icon: <FaCoffee />,
      color: "blue",
    },
    {
      title: "Tablet",
      count: `${data?.onlineKioskCount ?? 0}/${data?.kioskCount ?? 0}`,
      icon: <FaTabletAlt />,
      color: "purple",
    },
  ];

  const operations = data?.recentOperations || [];

  return (
    <PageShell>
      <div className="px-3 py-2.5 sm:p-4 md:p-6 border-b border-gray-100 shrink-0">
        <PageHeader
          icon={FaIndustry}
          iconColor="amber"
          title="Fabrika"
          subtitle="Üretim genel bakış ve canlı işlem akışı"
          stats={stats}
          isLoading={loading}
        />
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4 mt-4">
          {cards.map((c) => (
            <StatCard key={c.title} {...c} />
          ))}
        </div>
      </div>

      <ListView
        headers={TABLE_HEADERS}
        tableClassName="table-fixed"
        data={operations}
        isLoading={loading}
        loadingNode={
          <ListSkeleton
            columns={TABLE_HEADERS.map((h) => ({ label: h.label }))}
            rowCount={10}
            message="Fabrika verileri yükleniyor..."
          />
        }
        empty={
          <EmptyState
            message="Henüz işlem kaydı yok"
            suggestion="Tabletlerden işlem geldikçe burada görünecek"
          />
        }
        renderRow={(op) => (
          <tr key={op._id} className="border-b border-gray-100 hover:bg-gray-50">
            <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">
              {formatTime(op.createdAt)}
            </td>
            <td className="px-4 py-3 text-sm font-medium text-gray-900 truncate">
              {op.employeeSnapshot}
            </td>
            <td className="px-4 py-3 text-sm text-gray-700">
              {op.action?.name || "-"}
            </td>
            <td className="px-4 py-3 text-sm text-gray-700 truncate">
              {op.target?.label || op.target?.otherDescription || "-"}
              {op.machineSerialNo && (
                <span className="ml-2 font-mono text-xs text-gray-500">
                  {op.machineSerialNo}
                </span>
              )}
            </td>
            <td className="px-4 py-3 text-sm text-gray-500">
              {op.zone?.name || "-"}
            </td>
            <td className="px-4 py-3">
              <div className="flex items-center gap-2">
                {op.cancelled ? (
                  <span
                    title={op.cancelledReason || undefined}
                    className="inline-flex px-2 py-0.5 rounded-full text-xs font-semibold bg-red-50 text-red-700 border border-red-100"
                  >
                    İptal
                  </span>
                ) : (
                  <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-100">
                    Kayıtlı
                  </span>
                )}
                {canCancel && !op.cancelled && (
                  <RowActionButton
                    variant="danger"
                    onClick={() => {
                      setCancelReason("");
                      setCancelTarget(op);
                    }}
                  >
                    İptal Et
                  </RowActionButton>
                )}
              </div>
            </td>
          </tr>
        )}
        renderMobileCard={(op) => (
          <div
            key={op._id}
            className="bg-white border border-gray-200 rounded-xl p-3 flex flex-col gap-1"
          >
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold text-gray-900">
                {op.action?.name || "-"}
              </span>
              <span className="text-xs text-gray-500">{formatTime(op.createdAt)}</span>
            </div>
            <div className="text-xs text-gray-600">{op.employeeSnapshot}</div>
            <div className="text-xs text-gray-500">
              {op.target?.label || op.target?.otherDescription || "-"}
            </div>
          </div>
        )}
      />

      {/* Geç iptal modalı (BR-046 — sebep zorunlu) */}
      <Modal open={Boolean(cancelTarget)} onClose={() => setCancelTarget(null)} maxWidth="md">
        <Modal.Header
          title="İşlemi Geç İptal Et"
          subtitle={cancelTarget ? `${cancelTarget.employeeSnapshot} · ${cancelTarget.action?.name || ""}` : ""}
          onClose={() => setCancelTarget(null)}
        />
        <Modal.Body>
          <label className="block text-xs font-semibold text-gray-500 mb-1">
            İptal Sebebi <span className="text-red-500">*</span>
          </label>
          <textarea
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            rows={3}
            placeholder="Örn. Yanlış parça seçilmiş, çalışan bildirdi"
            className="w-full border border-input rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-200"
          />
          <p className="mt-1 text-xs text-gray-400">
            Kayıt silinmez; iptal olarak işaretlenir ve sebep denetim izine yazılır.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <button
            onClick={() => setCancelTarget(null)}
            className="px-3.5 py-2 text-sm font-semibold text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Vazgeç
          </button>
          <button
            onClick={submitCancel}
            disabled={!cancelReason.trim() || cancelling}
            className="px-3.5 py-2 text-sm font-semibold text-white bg-fifth rounded-lg hover:opacity-90 disabled:opacity-50"
          >
            {cancelling ? "İptal ediliyor..." : "İşlemi İptal Et"}
          </button>
        </Modal.Footer>
      </Modal>
    </PageShell>
  );
}
